import markedModule from 'marked';
import DOMPurify from 'dompurify';

const marked = markedModule.marked || markedModule;

const SITE_HOSTS = [
    'codingwithghonaim.com',
    'www.codingwithghonaim.com',
    'localhost',
];

const ARABIC_MONTHS = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

const DESIGNER_PREAMBLE = /انسخ النص ده مباشرة للصفحة:\s*/i;

function stripHtml(value) {
    return String(value || '').replace(/<[^>]+>/g, '');
}

export function slugifyHeading(text, used = {}) {
    const base = stripHtml(text)
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^\u0600-\u06FFa-zA-Z0-9.\-_]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        || 'section';

    let slug = base;
    let i = 2;
    while (used[slug]) {
        slug = `${base}-${i}`;
        i += 1;
    }
    used[slug] = true;
    return slug;
}

export function looksLikeMarkdown(text) {
    if (!text) {
        return false;
    }
    return /(^|\n)\s{0,3}#{1,3}\s+|(^|\n)>\s+|(^|\n)[-*]\s+|(^|\n)\d+\.\s+/.test(text);
}

export function stripDesignerPreamble(text) {
    if (!text) {
        return '';
    }
    const match = text.match(DESIGNER_PREAMBLE);
    if (!match) {
        return text.trim();
    }
    return text.slice(match.index + match[0].length).trim();
}

function isExternalHref(href) {
    if (!href) {
        return false;
    }
    if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('#')) {
        return false;
    }
    if (href.startsWith('/')) {
        return false;
    }
    try {
        const url = new URL(href, 'https://codingwithghonaim.com');
        return !SITE_HOSTS.includes(url.hostname);
    } catch (e) {
        return false;
    }
}

function createRenderer() {
    const renderer = new marked.Renderer();
    const usedIds = {};

    renderer.heading = function heading(text, level) {
        const safeLevel = Math.min(Math.max(level === 1 ? 2 : level, 2), 4);
        const id = slugifyHeading(text, usedIds);
        return `<h${safeLevel} id="${id}">${text}</h${safeLevel}>`;
    };

    renderer.blockquote = function blockquote(quote) {
        const inner = String(quote || '').trim();
        const warning = /^\s*<p>\s*(?:!|تحذير:)/i.test(inner);
        const type = warning ? 'warning' : 'info';
        return `<blockquote class="content-callout content-callout--${type}">${inner}</blockquote>`;
    };

    renderer.link = function link(href, title, text) {
        const safeHref = href || '#';
        const titleAttr = title ? ` title="${title}"` : '';
        const external = isExternalHref(safeHref) || safeHref.startsWith('https://wa.me');
        const rel = external ? ' rel="noopener noreferrer"' : '';
        const target = external ? ' target="_blank"' : '';
        return `<a href="${safeHref}"${titleAttr}${target}${rel}>${text}</a>`;
    };

    renderer.image = function image(href, title, text) {
        const titleAttr = title ? ` title="${title}"` : '';
        const alt = text || '';
        return `<img src="${href}" alt="${alt}"${titleAttr} loading="lazy" />`;
    };

    renderer.table = function table(header, body) {
        return `<div class="content-table-wrap"><table><thead>${header}</thead><tbody>${body}</tbody></table></div>`;
    };

    return renderer;
}

export function extractToc(markdown) {
    const usedIds = {};
    const items = [];
    const source = String(markdown || '').replace(/\r\n/g, '\n');

    source.split('\n').forEach((line) => {
        const match = /^(#{2,3})\s+(.+?)\s*#*\s*$/.exec(line);
        if (!match) {
            return;
        }
        const level = match[1].length;
        if (level !== 2) {
            return;
        }
        const text = match[2].replace(/\*\*|__/g, '').trim();
        items.push({
            id: slugifyHeading(text, usedIds),
            text,
            level,
        });
    });

    return items;
}

export function renderMarkdown(markdown) {
    const source = String(markdown || '').replace(/\r\n/g, '\n').trim();
    if (!source) {
        return '';
    }

    marked.setOptions({
        gfm: true,
        breaks: false,
        headerIds: false,
        mangle: false,
        renderer: createRenderer(),
    });

    const html = marked.parse(source);
    return DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        ADD_ATTR: ['target', 'rel', 'id', 'class', 'loading'],
        FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
        FORBID_ATTR: ['srcdoc', 'srcset'],
    });
}

export function formatArabicDate(value) {
    if (!value) {
        return '';
    }
    if (/[أ-ي]/.test(String(value))) {
        return String(value).replace(/^آخر تحديث:\s*/, '');
    }

    const parsed = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
    if (Number.isNaN(parsed.getTime())) {
        return String(value);
    }

    return `${parsed.getDate()} ${ARABIC_MONTHS[parsed.getMonth()]} ${parsed.getFullYear()}`;
}

export function resolvePageContent(localPage, remote) {
    const page = { ...localPage };
    if (!remote || typeof remote !== 'object') {
        return page;
    }

    if (remote.title) page.title = remote.title;
    if (remote.subtitle) page.subtitle = remote.subtitle;
    if (remote.lastUpdated || remote.updated_at || remote.updatedAt) {
        page.lastUpdated = remote.lastUpdated || remote.updated_at || remote.updatedAt;
    }
    if (remote.seoTitle) page.seoTitle = remote.seoTitle;
    if (remote.seoDescription) page.seoDescription = remote.seoDescription;

    const incoming = remote.contentMarkdown || remote.markdown || remote.text;
    if (incoming && looksLikeMarkdown(incoming) && /#{1,3}\s+/.test(incoming)) {
        page.contentMarkdown = stripDesignerPreamble(incoming);
    }

    return page;
}
